import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import BlogShell from "@/pages/blog/BlogShell";
import { POSTS, formatDate } from "@/pages/blog/posts";
import SEO from "@/components/SEO";

const BLOG_LIST_LD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Viyora Blog — Dental practice management, AI and DPDP for India",
  "url": "https://viyora.co.in/blog",
  "publisher": {
    "@type": "Organization",
    "name": "Viyora Tech",
    "logo": {
      "@type": "ImageObject",
      "url": "https://viyora.co.in/viyora-icon-512.png",
    },
  },
  "blogPost": POSTS.map((p) => ({
    "@type": "BlogPosting",
    "headline": p.title,
    "description": p.description,
    "datePublished": p.date,
    "author": { "@type": "Organization", "name": p.author },
    "url": `https://viyora.co.in/blog/${p.slug}`,
    "image": p.cover,
  })),
};

export default function BlogIndex() {
  const [featured, ...rest] = POSTS;

  return (
    <BlogShell>
      <SEO
        path="/blog"
        title="Blog — Practice management, AI and compliance for Indian dental clinics"
        description="Guides, comparisons and playbooks from the Viyora team on running a modern dental clinic in India — WhatsApp reminders, GST invoicing, DPDP compliance, and Viyora Copilot workflows."
        keywords="dental clinic blog India, dental software insights, DPDP dental clinic, WhatsApp reminders dentist, Viyora blog"
        structuredData={BLOG_LIST_LD}
      />

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10" data-testid="blog-index">
        <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-4">
          The Viyora Journal
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight max-w-3xl">
          Notes on running a modern dental practice in India.
        </h1>
        <p className="mt-6 text-ink-mid text-lg max-w-2xl leading-relaxed">
          Deep dives, comparisons and practical playbooks on AI, WhatsApp automation,
          GST invoicing and DPDP compliance — written by the team building Viyora.
        </p>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 pb-10">
        <Link
          to={`/blog/${featured.slug}`}
          data-testid={`blog-featured-${featured.slug}`}
          className="group grid lg:grid-cols-12 gap-8 items-center rounded-3xl bg-white border border-[#E2DCD0] hover:border-pine transition p-6 lg:p-8"
        >
          <div className="lg:col-span-6 relative rounded-2xl overflow-hidden">
            <img
              src={featured.cover}
              alt={featured.coverAlt}
              className="w-full h-64 lg:h-80 object-cover group-hover:scale-[1.02] transition"
              loading="eager"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-xs text-clay font-medium uppercase tracking-wider mb-4">
              <span>{featured.category}</span>
              <span className="w-1 h-1 rounded-full bg-pine/30" />
              <span>{formatDate(featured.date)}</span>
              <span className="w-1 h-1 rounded-full bg-pine/30" />
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {featured.readMins} min read
              </span>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-ink leading-[1.1] tracking-tight group-hover:text-clay transition">
              {featured.title}
            </h2>
            <p className="mt-4 text-ink-mid leading-relaxed">{featured.description}</p>
            <div className="mt-6 inline-flex items-center gap-1.5 text-ink font-medium">
              Read the article <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </div>
          </div>
        </Link>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group rounded-2xl bg-white border border-[#E2DCD0] hover:border-pine transition overflow-hidden flex flex-col"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.cover}
                  alt={post.coverAlt}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] text-clay font-medium uppercase tracking-wider mb-3">
                  <span>{post.category}</span>
                  <span className="w-1 h-1 rounded-full bg-pine/30" />
                  <span>{formatDate(post.date)}</span>
                </div>
                <h3 className="font-serif text-xl text-ink leading-snug tracking-tight group-hover:text-clay transition">
                  {post.title}
                </h3>
                <p className="mt-3 text-ink-mid text-sm leading-relaxed line-clamp-3 flex-1">
                  {post.description}
                </p>
                <div className="mt-5 text-sm text-ink font-medium inline-flex items-center gap-1.5">
                  Read more <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </BlogShell>
  );
}
